/**
 * What each derived analytics number means, in one place. Every entry is
 * written from the query that produces the number — when the query changes,
 * change the sentence here.
 */
export const METRIC_DEFINITIONS = {
  // Home
  home_active_markets: 'Set by hand in Admin → Home page. Not computed from market data.',
  home_team_members: 'Set by hand in Admin → Home page.',
  home_rollout_status: 'Set by hand in Admin → Home page.',
  home_active_now:
    'Open WebSocket connections right now, consumer app plus business app. Approximate: a connection that drops without disconnecting lingers for up to 24 hours.',

  // Founder scorecard
  verified_visits:
    'Live check-ins promoted to verified (enough on-site time) inside the window. Compared with the previous window of the same length.',
  active_users: 'Distinct users with at least one verified visit inside the window.',
  wau_mau:
    'Distinct users with a verified visit in the trailing 7 and 30 days. Fixed windows: they do not follow the date selector.',
  new_signups: 'Live (active, not deleted) accounts created inside the window.',
  activation:
    'Share of a signup cohort with a verified visit within 7 days of signing up. The cohort is shifted back 7 days so every signup has had its full week, so this lags the window by a week.',
  retention_d7:
    'Share of signups who came back with a verified visit more than 1 day and at most 7 days after signing up. Pooled over the newer half of the last 12 weekly cohorts that are at least 7 days old; the comparison is the older half. It does not follow the date selector.',
  retention_d30:
    'Share of signups who came back with a verified visit more than 1 day and at most 30 days after signing up. Pooled over the newer half of the last 12 weekly cohorts that are at least 30 days old; the comparison is the older half. It does not follow the date selector.',
  repeat_visitors:
    'Share of signups from the last 12 weekly cohorts (52 on All time) with two or more verified visits ever. It does not follow the date selector.',
  deal_venues: 'Distinct venues with a non-deleted deal whose start time falls inside the window.',
  deal_venue_visits: 'Verified visits inside the window at the venues that ran a deal in it.',
  friends_per_user:
    'Friendships per live account as of the end of the window: each friend pair counts once for each of its two users. The comparison is the same figure as of the start of the window.',
  pct_with_friend:
    'Share of live accounts with at least one friend as of the end of the window. The comparison is the same figure as of the start of the window.',

  // Economy health
  outstanding_liability:
    'Sum of every user’s current wallet balance: coins that could still be redeemed. A point-in-time figure, not affected by the date range.',
  mint_burn_ratio:
    'Coins minted ÷ coins burned in the window. Mint is the sum of positive ledger entries, burn the sum of negative ones. Above 3 is highlighted. Blank when nothing was burned.',
  visits_vs_checkins:
    'Attributed-visit coin awards ÷ check-in coin awards in the window. It counts ledger entries, so a visit or check-in that earned no coins is not in either number.',
  avg_coins_per_visit: 'Average coins paid per attributed-visit ledger entry in the window.',
  redemption_success_rate:
    'Confirmed ÷ (confirmed + expired) redemptions, by the day the redemption was created. Pending and refunded redemptions are left out.',
  expiry_rate: 'Expired ÷ all redemptions created at that venue in the window.',
  earn_breakdown: 'Coins minted (positive ledger entries) in the window, split by what earned them.',
  spin_distribution:
    'Actual is the share of spins in the window that paid each coin amount. Expected is that amount’s share of the configured spin weights.',
  suspicious_users:
    'Users currently flagged, grouped by flag reason. A current count, not affected by the date range.',
  top_markets:
    'Active markets ranked by attributed visits. Venues is distinct venues with an attributed visit. All time: not affected by the date range.',

  // Monitoring
  total_users: 'Every row in the users table, including deleted and inactive accounts. All time.',
  going_clicks: 'Check-in records with an "I’m Going" time set.',
  venue_hours: 'Sum of live check-in session durations, in hours.',
  window_new_users: 'Accounts created inside the window, including ones since deleted.',
  mtd_cost: 'AWS unblended cost from the 1st of this month to today, from Cost Explorer.',

  // Notification opens
  notif_open_rate:
    'Opened ÷ delivered, counting only notifications delivered since open tracking began. Blank when nothing was delivered.',
  notif_delivery_rate: 'Delivered ÷ attempted sends.',
  notif_fulfilled:
    'Notifications the app cleared because the user completed the action in-app, without necessarily tapping the push.',
  notif_addressable: 'Live accounts that both opened the app recently and have a registered device.',

  // Referral codes
  ref_signups_verified: 'Attributions counted in the window whose status is check-in verified.',
  ref_signups_total: 'Attributions counted in the window, excluding voided ones.',
  ref_owed:
    'Sum of the payout rate snapshotted on each attribution that is payout-eligible, not review-flagged and not void. Editing a code’s rate does not change it retroactively.',
  ref_owed_verified: 'The same sum, restricted to attributions that are check-in verified.',
  ref_deals_redeemed: 'Deal grants issued off the code in the window that have been used.',
  ref_review_flagged:
    'Attributions counted in the window that carry a review flag. They are held out of $ owed until cleared.',

  // Referral analytics
  ref_verified_share: 'Verified referrals ÷ counted referrals in the window.',
  ref_promoter_used: 'Promoter codes used in the window, out of every promoter code ever minted.',

  // Friend analytics
  friends_avg_per_user:
    'Friendships per live account, counting each pair once for each of its two users. A current figure, not affected by the date range.',
  friends_avg_per_connected:
    'The same average over only the users who have at least one friend. A current figure, not affected by the date range.',

  // B2B notification performance
  b2b_open_rate: 'Opened ÷ sent across the weeks shown, from per-recipient delivery rows.',
  b2b_attributed_visits:
    'Distinct users per venue per week who started a live check-in at the venue within 4 hours of a notification from it, and before that day’s closing time. Summed across venues and weeks.',

  // B2B user insights
  ui_total_users: 'Every consumer account ever created (account type "user"), including inactive ones.',
  ui_active_accounts: 'Consumer accounts whose status is active. Not the same as people using the app right now.',
  ui_new_today: 'Consumer accounts created in the trailing 24 hours.',
  ui_new_week: 'Consumer accounts created in the trailing 7 days.',
  ui_new_month: 'Consumer accounts created in the trailing 30 days.',
  ui_growth_wow:
    'Signups in the trailing 7 days against the 7 days before that: (this − previous) ÷ previous. Blank when the previous week had none.',
  ui_growth_mom:
    'Signups in the trailing 30 days against the 30 days before that: (this − previous) ÷ previous. Blank when the previous 30 days had none.',
} as const;

export type MetricKey = keyof typeof METRIC_DEFINITIONS;

const SOURCE_TYPE_LABELS: Record<string, string> = {
  attributed_visit: 'Venue visits',
  checkin: 'Check-ins',
  moment: 'Spark Moments',
  going: '"I\u2019m Going" taps',
  daily_open: 'Daily app open',
  profile_complete: 'Profile completed',
  subscribe: 'Venue subscriptions',
  share: 'Shares',
  spin: 'Spins',
  welcome_bonus: 'Welcome bonus',
  referral_signup: 'Referral signups',
  referral_welcome: 'Referral welcome',
  referral_completion: 'Referral completed',
  referral_checkin_verified: 'Referral verified visit',
  visit_streak: 'Visit streaks',
  redemption: 'Redemptions',
  redemption_bonus: 'Redemption bonus',
  gift_card_redemption: 'Gift card redemptions',
  gift_card_refund: 'Gift card refunds',
  admin_adjust: 'Admin adjustments',
};

/** A ledger `source_type` as a human label. Unknown types are title-cased rather than hidden. */
export function sourceTypeLabel(sourceType: string): string {
  return (
    SOURCE_TYPE_LABELS[sourceType] ??
    sourceType.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  );
}
